import axios, { type AxiosInstance, type AxiosResponse, type AxiosError } from 'axios'
import type { ApiResponse } from './client.types'
import { diagLog } from '@/services/diag/diag-trace'

const isDev = import.meta.env.DEV
const debugLog = (...args: unknown[]) => {
  if (isDev) console.warn(...args)
}
const debugError = (...args: unknown[]) => {
  if (isDev) console.error(...args)
}

/**
 * Track when the page last became visible. Mobile Chrome can drop session
 * cookies after a cross-app navigation (Phantom / Tonkeeper universal
 * link round-trip) — diagnostics needs to correlate 401s with the visibility
 * event so we can see if the drop happens specifically right after return.
 */
let lastVisibleAt = typeof document !== 'undefined' ? Date.now() : 0
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      lastVisibleAt = Date.now()
    }
  })
}

/**
 * Best-effort cookie-jar inventory. The `access_token` cookie is HttpOnly
 * so JS can never read its value, but we CAN see whether *any* cookie is
 * present and which non-HttpOnly cookies the browser is willing to expose.
 * Critical signal: if `document.cookie` is empty after a successful login,
 * the browser has dropped the entire jar (private session, ITP, etc.). If
 * non-HttpOnly cookies exist but the request still 401s, the issue is
 * specific to HttpOnly + SameSite=None + Secure handling.
 */
const inspectCookieJar = (): { jarLength: number; nonHttpOnlyNames: string[] } => {
  if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
    return { jarLength: 0, nonHttpOnlyNames: [] }
  }
  const cookieStr = document.cookie
  const names = cookieStr
    .split(';')
    .map((c) => c.trim().split('=')[0] ?? '')
    .filter((name) => name.length > 0)
  return { jarLength: cookieStr.length, nonHttpOnlyNames: names }
}

// Error response types
interface ErrorDetail {
  code?: string
  msg?: string
  details?: unknown
}

interface ErrorResponse {
  code?: string
  msg?: string
  details?: unknown
  detail?: ErrorDetail | ErrorDetail[] | string
}

export class ApiError extends Error {
  readonly status: number | null
  readonly code: string | null
  readonly details: unknown
  readonly retryAfterMs: number | null

  constructor(
    message: string,
    options: {
      status?: number | null
      code?: string | null
      details?: unknown
      retryAfterMs?: number | null
      cause?: unknown
    } = {}
  ) {
    super(message, { cause: options.cause })
    this.name = 'ApiError'
    this.status = options.status ?? null
    this.code = options.code ?? null
    this.details = options.details
    this.retryAfterMs = options.retryAfterMs ?? null
  }
}

const parseRetryAfterMs = (value: unknown): number | null => {
  if (typeof value !== 'string' || value === '') return null
  const seconds = Number(value)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const retryAt = Date.parse(value)
  return Number.isFinite(retryAt) ? Math.max(0, retryAt - Date.now()) : null
}

const retryAfterFromDetails = (details: unknown): number | null => {
  if (!details || typeof details !== 'object' || !('retry_after_ms' in details)) return null
  const value = details.retry_after_ms
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : null
}

const optionalString = (value: unknown): string | null => (typeof value === 'string' ? value : null)

const structuredErrorDetail = (detail: ErrorResponse['detail']): ErrorDetail | null => {
  if (!detail || Array.isArray(detail) || typeof detail !== 'object') return null
  return detail
}

const validationErrorMessage = (detail: ErrorResponse['detail']): string | null => {
  if (!Array.isArray(detail)) return null
  return detail
    .map((item) => item.msg)
    .filter(Boolean)
    .join(', ')
}

const firstString = (values: (string | null)[]): string | null => {
  for (const value of values) {
    if (value !== null) return value
  }
  return null
}

const selectRetryAfterMs = (details: unknown, header: unknown): number | null => {
  const detailsValue = retryAfterFromDetails(details)
  return detailsValue ?? parseRetryAfterMs(header)
}

const normalizedErrorMessage = (
  data: ErrorResponse | undefined,
  detail: ErrorResponse['detail'],
  structuredDetail: ErrorDetail | null
): string | null =>
  firstString([
    optionalString(data?.msg),
    optionalString(structuredDetail?.msg),
    validationErrorMessage(detail),
    optionalString(detail),
  ])

const normalizedErrorCode = (
  data: ErrorResponse | undefined,
  structuredDetail: ErrorDetail | null
): string | null =>
  firstString([optionalString(data?.code), optionalString(structuredDetail?.code)])

const normalizedErrorDetails = (
  data: ErrorResponse | undefined,
  structuredDetail: ErrorDetail | null
): unknown => data?.details ?? structuredDetail?.details ?? data

export const normalizeApiError = (error: AxiosError<ErrorResponse>): ApiError => {
  const data = error.response?.data
  const detail = data?.detail
  const structuredDetail = structuredErrorDetail(detail)
  const details = normalizedErrorDetails(data, structuredDetail)
  const message = normalizedErrorMessage(data, detail, structuredDetail)
  const code = normalizedErrorCode(data, structuredDetail)
  const retryAfterMs = selectRetryAfterMs(details, error.response?.headers['retry-after'])

  return new ApiError(message ?? error.message, {
    status: error.response?.status,
    code,
    details,
    retryAfterMs,
    cause: error,
  })
}

/**
 * A 401 from the TON auth endpoints means "proof/payload invalid" and the
 * caller is responsible for handling it (showing a retry message, etc.) —
 * it is NOT a signal that the current browser session expired. Firing the
 * global auth-expired callback here cascades into tonConnectUI.disconnect()
 * in the middle of the verify-flow and breaks mobile TonConnect sign-ins.
 */
export const shouldTriggerAuthExpired = (status: number, url: string): boolean => {
  if (status !== 401) return false
  const isTonAuthEndpoint = url.startsWith('/auth/ton/start') || url.startsWith('/auth/ton/verify')
  return !isTonAuthEndpoint
}

class ApiClient {
  private client: AxiosInstance
  private baseURL: string
  private authExpiredCallback?: () => void
  private authExpiredListeners = new Set<() => void>()
  private authContext = 0
  private requestAuthContexts = new WeakMap<object, number>()

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.spotty.one'

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor for debugging
    this.client.interceptors.request.use(
      (config) => {
        this.requestAuthContexts.set(config, this.authContext)
        debugLog(`🚀 API Request: ${String(config.method?.toUpperCase())} ${String(config.url)}`)
        return config
      },
      (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        debugLog(
          `✅ API Response: ${String(response.config.method?.toUpperCase())} ${String(response.config.url)} - ${String(response.status)}`
        )
        // Baseline log: when /auth/ton/verify succeeds the backend has
        // just stamped a fresh JWT cookie. Any 401 after this entry that
        // wasn't preceded by another verify is the browser dropping the
        // cookie. Pair these with `apiClient.401.captured` to triangulate
        // when (and on which navigation) the cookie disappears.
        try {
          const url = response.config.url ?? ''
          if (url.startsWith('/auth/ton/verify') && response.status === 200) {
            const cookieJar = inspectCookieJar()
            diagLog('apiClient.auth.cookieSet', {
              cookieJarLength: cookieJar.jarLength,
              nonHttpOnlyCookieNames: cookieJar.nonHttpOnlyNames.slice(0, 8),
              pageHref:
                typeof window !== 'undefined' && window.location.href
                  ? window.location.href.split('?')[0]
                  : '',
              visibilityState:
                typeof document !== 'undefined' ? document.visibilityState : 'unknown',
            })
          }
        } catch {
          // Diagnostics must never break the response path.
        }
        return response
      },
      (error: AxiosError<ErrorResponse>) => {
        debugError('❌ API Error:', error)

        if (error.response) {
          const { status } = error.response

          // Handle 401 Unauthorized - session expired.
          const requestContext = error.config
            ? this.requestAuthContexts.get(error.config)
            : undefined
          if (
            shouldTriggerAuthExpired(status, error.config?.url ?? '') &&
            requestContext === this.authContext
          ) {
            debugLog('🔐 Session expired (401), triggering auth expired callback')
            // Capture context every 401 — we're hunting for a mobile bug
            // where the JWT cookie disappears between successful auth and
            // the next protected request, but JWT TTL is 100h so cookies
            // SHOULDN'T be expiring. The data here lets us correlate 401
            // bursts with visibility transitions (= Phantom / Tonkeeper
            // round-trips), see whether the entire cookie jar is empty
            // (browser purge) vs only the access_token missing (HttpOnly-
            // specific drop), and identify the specific URL pattern that
            // triggers it. Drop once cookie loss is understood and fixed.
            try {
              const cookieJar = inspectCookieJar()
              const visibilityState =
                typeof document !== 'undefined' ? document.visibilityState : 'unknown'
              const msSinceVisible = lastVisibleAt > 0 ? Date.now() - lastVisibleAt : -1
              const requestUrl = (error.config?.url ?? '').split('?')[0]
              const requestMethod = (error.config?.method ?? 'GET').toUpperCase()
              const pageHref =
                typeof window !== 'undefined' && window.location.href
                  ? window.location.href.split('?')[0]
                  : ''
              const referrer =
                typeof document !== 'undefined' && typeof document.referrer === 'string'
                  ? document.referrer.split('?')[0]
                  : ''
              const withCredentials = error.config?.withCredentials !== false
              diagLog('apiClient.401.captured', {
                requestUrl,
                requestMethod,
                visibilityState,
                msSinceVisible,
                cookieJarLength: cookieJar.jarLength,
                cookieJarEmpty: cookieJar.jarLength === 0,
                nonHttpOnlyCookieNames: cookieJar.nonHttpOnlyNames.slice(0, 8),
                pageHref,
                referrer,
                withCredentials,
              })
            } catch {
              // Diagnostics must never break the request path.
            }
            this.authExpiredCallback?.()
            for (const callback of this.authExpiredListeners) {
              callback()
            }
          } else if (status === 401) {
            debugLog('TON auth endpoint returned 401 — caller handles it')
          }

          return Promise.reject(normalizeApiError(error))
        }

        return Promise.reject(normalizeApiError(error))
      }
    )
  }

  setAuthExpiredCallback(callback: () => void): void {
    this.authExpiredCallback = callback
  }

  invalidateAuthContext(): void {
    // A late 401 from a previous account must not sign out the current account.
    this.authContext += 1
  }

  addAuthExpiredCallback(callback: () => void): () => void {
    this.authExpiredListeners.add(callback)
    return () => {
      this.authExpiredListeners.delete(callback)
    }
  }

  // Generic API call wrapper
  async apiCall<T>(apiCall: () => Promise<AxiosResponse<T>>): Promise<ApiResponse<T>> {
    try {
      const response = await apiCall()
      return {
        success: true,
        data: response.data,
      }
    } catch (error: unknown) {
      debugError('API call failed:', error)

      let errorMessage = 'Unknown error occurred'
      let errorCode: string | undefined
      let errorDetail: unknown
      let status: number | undefined

      if (error instanceof ApiError) {
        errorMessage = error.message
        errorCode = error.code ?? undefined
        errorDetail = error.details
        status = error.status ?? undefined
      } else {
        const axiosError = error as AxiosError<ErrorResponse>
        status = axiosError.response?.status
        errorDetail = axiosError.response?.data
        errorCode = axiosError.response?.data.code
      }

      const axiosError = error as AxiosError<ErrorResponse>
      if (!(error instanceof ApiError) && axiosError.response?.data.detail) {
        const detail = axiosError.response.data.detail
        // Handle validation errors (array)
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: ErrorDetail) => err.msg).join(', ')
        }
        // Handle structured errors with code/msg
        else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg
        }
        // Handle simple string errors
        else if (typeof detail === 'string') {
          errorMessage = detail
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      return {
        success: false,
        error: errorMessage,
        errorCode,
        errorDetail,
        status,
      }
    }
  }

  // Generic request methods
  get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.apiCall(() => this.client.get(url, { params }))
  }

  post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.apiCall(() => this.client.post(url, data))
  }

  patch<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.apiCall(() => this.client.patch(url, data))
  }

  async getData<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params })
    return response.data
  }

  async postData<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data)
    return response.data
  }

  async putData<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data)
    return response.data
  }

  async patchData<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data)
    return response.data
  }

  // Test session validity
  async testSession(): Promise<boolean> {
    try {
      const response = await this.get('/auth/users/me')
      return response.success
    } catch (error) {
      debugError('❌ Session test failed:', error)
      return false
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
